import { FC, useEffect, useState } from "react";

import { Button, Divider, Flex, Input, Menu } from "antd";
import { FilterDropdownProps } from "antd/es/table/interface";

import axiosInstance from "../axiosInstance";
import { Field } from "../services/collectionFields";

interface SearchFilterProps {
  field: string;
  collectionId: string;
  dropdownProps: FilterDropdownProps;
  existingFilters?: Record<string, string[]>;
  fields?: Field[];  // Add fields to determine field types
}

const SearchFilter: FC<SearchFilterProps> = ({
  field,
  collectionId,
  dropdownProps,
  existingFilters = {},
  fields = [],
}) => {
  const [searchText, setSearchText] = useState<string>("");
  const [searchResult, setSearchResult] = useState<
    { key: string; label: string }[]
  >([]);

  useEffect(() => {
    const filterField = async (filter: string) => {
      // Build the query with existing filters
      // Tri côté Directus : anti-alphabétique (décroissant) uniquement pour le champ
      // source_file_name (Source File Name) de transactions_fao, ordre par défaut ailleurs.
      const sortParam =
        field === "source_file_name" ? `&sort=-${field}` : "";
      let query = `https://melcarnedirectus2.digipulse.ch/items/${collectionId}?aggregate[count]=*&meta=filter_count&groupBy=${field}${sortParam}&limit=100`;
      
      // Add search filter for the current field
      if (filter) {
        query += `&filter[${field}][_icontains]=${filter}`;
      }
      
      // Add existing filters (except for the current field to avoid conflicts)
      Object.entries(existingFilters).forEach(([filterField, filterValues]) => {
        if (filterField !== field && filterValues && filterValues.length > 0) {
          // Handle different filter formats
          const cleanValues = filterValues.map(v => v.replace(/[\*=]+$/, '')); // Remove trailing asterisks or equals signs
          
          // Check if this is a date/datetime field using field metadata
          const fieldInfo = fields.find(f => f.name === filterField);
          const isDateField = fieldInfo && (fieldInfo.ui === "datetime" || fieldInfo.type === "date" || fieldInfo.type === "timestamp");
          
          if (isDateField) {
            // Date fields should use _between operator and convert DD/MM/YYYY to YYYY-MM-DD
            if (cleanValues.length === 2) {
              try {
                const convertedDates = cleanValues.map(dateStr => {
                  const parts = dateStr.split('/');
                  if (parts.length === 3) {
                    return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert DD/MM/YYYY to YYYY-MM-DD
                  }
                  return dateStr;
                });
                query += `&filter[${filterField}][_between]=${convertedDates.join(',')}`;
              } catch (error) {
                console.error('Error converting date filter:', error);
                // Fallback to original behavior if conversion fails
                query += `&filter[${filterField}][_in]=${cleanValues.join(',')}`;
              }
            } else {
              // Single date value, use _eq
              try {
                const dateStr = cleanValues[0];
                const parts = dateStr.split('/');
                if (parts.length === 3) {
                  const convertedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
                  query += `&filter[${filterField}][_eq]=${convertedDate}`;
                } else {
                  query += `&filter[${filterField}][_eq]=${dateStr}`;
                }
              } catch (error) {
                console.error('Error converting date filter:', error);
                query += `&filter[${filterField}][_in]=${cleanValues.join(',')}`;
              }
            }
          } else if (filterField === "AllCheckboxes") {
            // Skip AllCheckboxes field - it has special handling elsewhere and shouldn't be processed here
            // to avoid conflicts with complex filter structures
            return;
          } else if (filterField === "Type") {
            // Type field should use _icontains for better text matching
            if (cleanValues.includes("Vide (Sans valeur)")) {
              const nonNullValues = cleanValues.filter(v => v !== "Vide (Sans valeur)");
              if (nonNullValues.length > 0) {
                if (nonNullValues.length === 1) {
                  query += `&filter[${filterField}][_icontains]=${encodeURIComponent(nonNullValues[0])}`;
                } else {
                  // For multiple values with Type, we can still use _in but it's better to use _icontains for each
                  // Since Directus OR logic is complex, let's use _in as fallback for multiple values
                  query += `&filter[${filterField}][_in]=${nonNullValues.join(',')}`;
                }
              }
            } else {
              // For Type field, use _icontains for single values, _in for multiple
              if (cleanValues.length === 1) {
                query += `&filter[${filterField}][_icontains]=${encodeURIComponent(cleanValues[0])}`;
              } else {
                query += `&filter[${filterField}][_in]=${cleanValues.join(',')}`;
              }
            }
          } else if (cleanValues.includes("Vide (Sans valeur)")) {
            // Handle null values - this is more complex and might need specific handling
            const nonNullValues = cleanValues.filter(v => v !== "Vide (Sans valeur)");
            if (nonNullValues.length > 0) {
              query += `&filter[${filterField}][_in]=${nonNullValues.join(',')}`;
            }
          } else {
            // Check field type to determine appropriate operator
            const fieldInfo = fields.find(f => f.name === filterField);
            const isTextField = fieldInfo && (fieldInfo.type === "string" || fieldInfo.type === "text" || fieldInfo.ui === "input" || fieldInfo.ui === "input-multiline");
            const isNumericField = fieldInfo && (fieldInfo.type === "integer" || fieldInfo.type === "float" || fieldInfo.ui === "numeric");
            
            if (cleanValues.length === 2 && (isNumericField || isDateField)) {
              // Use _between for range selectors (2 values for numeric or date fields)
              query += `&filter[${filterField}][_between]=${cleanValues.join(',')}`;
            } else if (isTextField && cleanValues.length === 1) {
              // Use _icontains for single text values for better partial matching
              query += `&filter[${filterField}][_icontains]=${encodeURIComponent(cleanValues[0])}`;
            } else {
              // Use _in for multiple values or other cases
              query += `&filter[${filterField}][_in]=${cleanValues.join(',')}`;
            }
          }
        }
      });

      const response = await axiosInstance.get(query);

      setSearchResult([
        { key: "Vide (Sans valeur)", label: "Vide (sans valeur)" },
        ...response.data.data
          .map((v: any) => v[field] as string)
          .filter((v: any) => !!v)
          .map((v: any) => ({ key: v, label: v })),
      ]);
    };

    filterField(searchText);
  }, [searchText, field, collectionId, existingFilters]);

  return (
    <Flex vertical style={{ padding: 8, width: 250 }}>
      <Input
        placeholder="Résultats limités à 50 éléments)"
        onChange={(event) => setSearchText(event.target.value)}
        allowClear
      />
      <Divider style={{ marginTop: 8, marginBottom: 0 }} />
      <Menu
        selectedKeys={dropdownProps.selectedKeys as string[]}
        items={searchResult}
        multiple
        style={{ maxHeight: 150 }}
        onSelect={({ selectedKeys }) =>
          dropdownProps.setSelectedKeys(selectedKeys)
        }
        onDeselect={({ selectedKeys }) =>
          dropdownProps.setSelectedKeys(selectedKeys)
        }
      />
      <Divider style={{ marginTop: 0, marginBottom: 8 }} />
      <Flex justify="space-between">
        <Button
          onClick={() => dropdownProps.setSelectedKeys([])}
          type="link"
          size="small"
          disabled={dropdownProps.selectedKeys.length === 0}
        >
          Réinitialiser
        </Button>
        <Button
          onClick={() => dropdownProps.confirm()}
          type="primary"
          size="small"
        >
          OK
        </Button>
      </Flex>
    </Flex>
  );
};

export default SearchFilter;
